import React, { useState, useRef, memo, useMemo, CSSProperties, useEffect } from 'react';
import leftDefIcon from '@/pages/intelligence/assets/svg/mapChart/left-default.svg';
import leftDisableIcon from '@/pages/intelligence/assets/svg/mapChart/left-disable.svg';
import rightDefIcon from '@/pages/intelligence/assets/svg/mapChart/right-default.svg';
import rightDisableIcon from '@/pages/intelligence/assets/svg/mapChart/right-disable.svg';
import EchartMap from './components/EchartMap';
import { unitConversionNew } from '@/pages/dashboard/utils/utils';
import { EchartMapBarProps } from './components/EchartMap/EchartMap';
import './index.scss';
import { ChangeSize } from '../../MarketSize/utils';
import { getLocale } from 'umi';
import { LANGUAGE_CN } from '@/pages/home/_config/config';

type IMarkLegendProps<S, N> = {
  type: S;
  text: (...angs: any[]) => void;
  value: N;
  color: S;
};
type IVisual<S, N> = { value: N; color: S };

interface IEacherLine<S = string, N = number> {
  style?: CSSProperties;
  // modal开启状态下的title
  fullScreenTitle: S;
  // 数据源，需要自行处理,有机会对数据源传入格式进行统一，目前看起来TS不生效，约束不了,容易有未知bug产生
  dataRes: any[];
  // 左侧的合计
  mapRevenue?: S;
  // 右侧的合计
  mapGrowth?: S;
  // 地图容器的高度
  height: Readonly<N>;
  // legend配置
  marketLegend: Partial<IMarkLegendProps<S, N>[]>;
  // 类别色块配置
  visualMapColorFun?: Partial<IVisual<S, N>>;
  // 地图点击的回调
  handleClickFun: (...args: any[]) => void;
  // toolTip格式化回调
  tooltipMapFun: <T extends unknown>(...args: T[]) => void;
  // 自定义lenged元素
  customVisualMapColorFun?: React.ReactNode;
  // 其他自定义
  [key: string]: unknown;
}
interface IPagination {
  className: string;
  recordCount: number;
  onPagingChange: (...param: any[]) => void;
}
type IEchartProps = Partial<Omit<EchartMapBarProps, 'data' | 'showModel'>> &
  IEacherLine;

export default memo((props: IEchartProps) => {
  const {
    fullScreenTitle,
    style,
    dataRes,
    mapRevenue,
    mapGrowth,
    height: parentHeight,
    marketLegend,
    visualMapColorFun,
    customVisualMapColorFun,
    handleClickFun,
    tooltipMapFun,
    ...resetProps
  } = props;
  const locale = getLocale();
  // console.log('MAP props', props);

  const legendHeightRef = useRef<any>(null);
  const mapCardRef = useRef<any>(null);

  const [index, setIndex] = useState(0);
  // const size = useSize()
  const size = ChangeSize();

  const [legendBox, setLegendBox] = useState(<></>);
  useEffect(() => {
    setLegendBox(renderLegends(marketLegend));
  }, [
    mapCardRef?.current?.clientWidth,
    index,
    mapGrowth,
    mapRevenue,
    marketLegend,
  ]);

  // TODO:待处理,可以按照这种形式传入,也可以自定义
  const renderLegends = useMemo(
    () =>
      (params: { [key in string]: any } = marketLegend) => {
        const list = params?.map((item: any) => {
          const textFun = locale === LANGUAGE_CN ? item?.text_cn || item.text : item.text;
          return (
          <div>
            <span
              className="MarketSize-EchartMap-LengedIcon"
              style={{ background: item.color }}
            ></span>
            {textFun(
              unitConversionNew(mapRevenue, { isFloat: true, fixedNum: 0 }),
              mapGrowth,
            )}
          </div>
          )
        });
        const _list = [];
        for (let i = 0; i < list.length; i = i + 2) {
          _list.push(
            <div className="MarketSize-EchartMap-Lenged">
              <div>{list[i]}</div>
              {i + 1 < list.length ? <div>{list[i + 1]}</div> : null}
            </div>,
          );
        }
        const isMaxLegendWidth = mapCardRef?.current?.clientWidth > 650;
        return (
          <div ref={legendHeightRef} className="dbi-echartmap-legendbox">
            <div className="dbi-echartmap-legendbox-main">
              <ul
                style={{
                  transform: `translateX(${-230 * index}px)`,
                }}
              >
                {_list?.length > 0 &&
                  _list?.map((item: any, idx: number) => (
                    <li key={idx}>{item}</li>
                  ))}
              </ul>
            </div>
            {isMaxLegendWidth ? null : (
              <ChartPagination
                className="dbi-echartmap-legendbox-pagination"
                recordCount={_list.length}
                onPagingChange={(param: { pageIndex: number }) => {
                  const { pageIndex } = param;
                  setIndex(pageIndex - 1);
                }}
              />
            )}
          </div>
        );
      },
    [index, mapGrowth, mapRevenue, marketLegend, locale],
  );

  const fullScreenLegendChildren = useMemo(
    () => (
      <div className="MarketSize-EchartMap-Lenged">
        {marketLegend.map((item) => {
          const textFun = locale === LANGUAGE_CN ? item?.text_cn || item?.text : item?.text;

          return (
          <div>
            <span
              className="MarketSize-EchartMap-LengedIcon"
              style={{ background: item?.color }}
            ></span>
            {textFun?.(
              unitConversionNew(mapRevenue, { isFloat: true, fixedNum: 0 }),
              mapGrowth || '',
            )}
          </div>
          )
        })}
      </div>
    ),
    [mapGrowth, mapRevenue, marketLegend, locale],
  );

  return (
    <div className="eacherMap" style={style} ref={mapCardRef}>
      <EchartMap
        fullScreenTitle={fullScreenTitle}
        data={dataRes}
        mapHeight={'100%'}
        visualMapColorFun={visualMapColorFun || []}
        tooltipMapFun={tooltipMapFun}
        handleClickFun={handleClickFun}
        className={'MarketSize-EchartMap'}
        doubleClick={true}
        zoomButton={true}
        fullScreen={true}
        legendChildren={customVisualMapColorFun || legendBox}
        fullScreenLegendChildren={fullScreenLegendChildren}
        showModel={false}
        {...resetProps}
      />
    </div>
  );
});

function ChartPagination(props: IPagination) {
  const { className, recordCount, onPagingChange } = props;
  const [pageIndex, setPageIndex] = useState(1);
  const pageChange = (index: number, type: 'pre' | 'next') => {
    if (index > 0 && index <= recordCount) {
      setPageIndex(index);
      onPagingChange({ pageIndex: index, type });
    }
  };
  return (
    <div className={className}>
      <div
        className="dbi-echartmap-legendbox-pagination-icon"
        style={{
          cursor: pageIndex === 1 ? 'default' : 'pointer',
        }}
      >
        <img
          src={pageIndex === 1 ? leftDisableIcon : leftDefIcon}
          onClick={() => {
            pageChange(pageIndex - 1, 'pre');
          }}
        />
      </div>
      <span className="dbi-echartmap-legendbox-pagination-text">
        {pageIndex}/{recordCount}
      </span>
      <div
        className="dbi-echartmap-legendbox-pagination-icon"
        style={{
          cursor: pageIndex === recordCount ? 'default' : 'pointer',
        }}
      >
        <img
          src={pageIndex === recordCount ? rightDisableIcon : rightDefIcon}
          onClick={() => {
            pageChange(pageIndex + 1, 'next');
          }}
        />
      </div>
    </div>
  );
}
